import {
  AppointmentStatusCodes,
  CreateCaseDto,
  DataBlockTypeDto,
  DataBlockVariant,
  DataBlockWithValuesDto,
  DeviceModel,
  FileType,
  MeasurementMappingDto,
  PatientDto,
  PatientListDto,
  PatientState,
  PatientStatus,
  QueryFactory,
  ReadFileData,
  UIConfigType,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  IPatientWithCase,
  PatientSearch,
} from '@lmt/unicorn-shared/app/components/common/patient/PatientSearch';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { getObjectHash } from '@lmt/unicorn-shared/app/components/merge-assistant/form/utils/hash-utils';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import {
  getImportFile,
  getPdfFile,
} from '@lmt/unicorn-shared/app/features/measurements/selectors';
import { MeasuredValue } from '@lmt/unicorn-shared/app/features/measurements/types';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import {
  localizeCaseTitle,
  localizeVisitType,
} from '@lmt/unicorn-shared/app/helpers/localization/translations';
import {
  getVisitOption,
  VisitOption,
} from '@lmt/unicorn-shared/app/helpers/visit-queries';
import ErrorIcon from '@lmt/unicorn-shared/app/icons/error-circle.svg?react';
import SettingsIcon from '@lmt/unicorn-shared/app/icons/gear.svg?react';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { useEventCallback } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  addDays,
  addSeconds,
  format,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import Logger from 'js-logger';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { MeasurementActions } from '../../features/measurements/reducer';
import { CaseData, useCases } from '../../helpers/case-queries';
import { Input } from '../common/ui-kit/inputs/Input';

import { useModal } from '../common/ui-kit/modal/useModal';
import { getFullName } from '../patients/patient-form/common/common';
import { InputComponentForPatientSearch } from './components/InputComponentForPatientSearch';
import { RadioButton as SelectTypeRadioButton } from './components/RadioButton';

import styles from './MiniScreenImporter.module.scss';
import { deleteMiniScreenPdfFile } from './utils/file-operations';
import { ImportData, importMeasurement } from './utils/import';
import {
  getPatientInfoFromMeasuredValues,
  getPdfFilePath,
  getTemplateMeasurementType,
  getTemplateName,
  PatientInfo,
} from './utils/info-parsing';
import { mapMeasuredValues } from './utils/mapping';
import { parseData } from './utils/parsing';

const defaultDeviceModel = DeviceModel.MiniScreen;

const MiniScreenImporter: FC<FormComponentProps> = (props) => {
  const { formType, formId, tryCompleteForm, loadBlocking } = props;
  const tabs = useTabs();
  const dispatch = useDispatch();
  const i18next = useTranslation();
  const globalForms = useGlobalForms();
  const queryClient = useQueryClient();
  const measuredValues = useUniSelector(
    (state) => state.measurement.measuredValues,
  );
  const dataBlockTypes = useEnabledDataBlockTypes();
  const [selectedMeasurementType, setMeasurementType] = useState<
    DataBlockTypeDto | undefined
  >(undefined);
  const [selectedPatient, setSelectedPatient] = useState<
    PatientListDto | PatientDto | null
  >(null);
  const [suggestedPatients, setSuggestedPatients] = useState<
    IPatientWithCase[]
  >([]);
  const [error, setError] = useState<Error | undefined>(undefined);
  const claims = useClaims();
  const readFile = useUniSelector((state) => state.measurement.readFile);
  const [currentCase, setCurrentCase] = useState<CaseData | null>(null);
  const [currentVisit, setCurrentVisit] = useState<VisitOption | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [isDirty, setDirty] = useState<boolean>(false);
  const [isInvalidPatientInfo, setIsInvalidPatientInfo] =
    useState<boolean>(false);

  // measurement with the same type and date for the same patient (if such measurement exists)
  // (values can be the same or different)
  const [existingMeasurement, setExistingMeasurement] =
    useState<DataBlockWithValuesDto | null>(null);

  const importFile = useUniSelector(getImportFile);
  const pdfFile = useUniSelector(getPdfFile);

  const isCaseCreating = useRef(false); // to prevent multiple case creating
  const isVisitCreating = useRef(false); // to prevent multiple visit creating
  useEffect(() => {
    return () => {
      // to reset the readFile data and prevent Bug 48721: "Measurement from Miniscreen" form opens after saving autologout/language settings
      dispatch(MeasurementActions.importCancelled());
    };
  }, []);

  const measurementTypes = useMemo(
    () =>
      dataBlockTypes?.filter(
        (x) =>
          !x.isDeactivated &&
          x.variant === DataBlockVariant.DeviceMeasurement &&
          x.device === DeviceModel.MiniScreen,
      ) ?? [],
    [dataBlockTypes],
  );

  const { data: importSettings, isLoading: isSettingsQueryLoading } =
    QueryFactory.MeasurementImportSettingsQuery.useGetQuery(
      DeviceModel.MiniScreen,
    );

  const casesQuery = useCases(selectedPatient?.id);
  const cases: CaseData[] = useMemo(
    () => casesQuery?.data ?? [],
    [casesQuery?.data],
  );

  const visitTypes = useVisitTypes();

  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery(
    {
      patientId: selectedPatient?.id,
    },
    { enabled: !!selectedPatient?.id },
  );

  const visits: VisitOption[] = useMemo(() => {
    if (!visitsQuery.data) {
      return [];
    }
    return (
      visitsQuery.data
        ?.filter(
          (x) =>
            x.statusId !== AppointmentStatusCodes.Canceled &&
            x.statusId !== AppointmentStatusCodes.Skipped,
        )
        .filter((x) => (currentCase ? x.caseId === currentCase.id : true))
        .map((x) =>
          getVisitOption(x, localizeVisitType(visitTypes, x.typeId)),
        ) ?? []
    );
  }, [
    visitsQuery.data,
    visitsQuery.isStale,
    visitsQuery.isFetched,
    visitTypes,
    currentCase,
  ]);

  const measurementFieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery(
    { dataBlockTypeId: selectedMeasurementType?.id },
    {
      enabled: !!selectedMeasurementType,
    },
  );

  const mappingsQuery = QueryFactory.MeasurementMappingQuery.useGetQuery(
    {
      measurementTypeId: selectedMeasurementType?.id ?? 0,
      deviceModel: defaultDeviceModel,
    },
    {
      enabled: !!selectedMeasurementType,
      select: useCallback(
        (data: MeasurementMappingDto[]) => data.filter((x) => x.isActive),
        [],
      ),
    },
  );

  const existingMeasurementFilesQuery =
    QueryFactory.FileQuery.useSearchFilesQuery(
      {
        patientId: selectedPatient?.id,
        dataBlockId: existingMeasurement?.id,
        fileType: FileType.DeviceReport,
      },
      {
        enabled: !!selectedPatient && !!existingMeasurement,
      },
    );

  useEffect(() => {
    if (!visitsQuery.isFetched || visitsQuery.isStale) {
      return;
    }
    if (visits.length) {
      if (visits.find((visitOption) => visitOption.id === currentVisit?.id)) {
        return;
      }
      // we try to find an appropriate visit within the interval from import settings
      // first we try to find the nearest visit before measurement date (backward)
      const visitsBeforeMeasurement = visits
        .filter(
          (x) =>
            x.start <= patientInfo!.startDate &&
            x.start >=
              addDays(
                patientInfo!.startDate,
                -importSettings!.visitSearchBackwardDaysCount,
              ),
        )
        .sort((a, b) => b.start!.getTime() - a.start!.getTime());
      let visit = visitsBeforeMeasurement.length
        ? visitsBeforeMeasurement[0]
        : null;

      // if no result we search for visit after measurement date
      if (!visit) {
        const visitsAfterMeasurement = visits
          .filter(
            (x) =>
              x.start >= patientInfo!.startDate &&
              x.start <=
                addDays(
                  patientInfo!.startDate,
                  importSettings!.visitSearchForwardDaysCount,
                ),
          )
          .sort((a, b) => a.start!.getTime() - b.start!.getTime());
        visit = visitsAfterMeasurement.length
          ? visitsAfterMeasurement[0]
          : null;
      }
      if (visit) {
        setCurrentVisit(visit);
        return;
      }
    }
    setCurrentVisit(null);
    if (!selectedPatient) {
      return;
    }
    if (!importSettings?.createVisitAutomatically) {
      return;
    }
    void createNewVisit(); // if visit was not found we create a new one
  }, [visits]);

  useEffect(() => {
    const visitCase = cases.find((x) => x.id === currentVisit?.caseId);
    if (visitCase && visitCase.id !== currentCase?.id) {
      setCurrentCase(visitCase);
    }
  }, [currentVisit, cases]);

  const createNewVisit = useEventCallback(async () => {
    if (isVisitCreating.current) {
      return;
    }
    if (!importSettings) {
      console.error(
        'No measurement import settings found. Cannot create visit.',
      );
      return;
    }
    const visitStart = addSeconds(
      startOfDay(patientInfo!.startDate),
      importSettings.visitStartTimeInSecondsSinceMidnight,
    );
    const visitEnd = addSeconds(
      visitStart,
      importSettings.visitLengthInSeconds,
    );
    const visitType = visitTypes
      .filter((v) => !v.isDeleted)
      .find((x) => x.id === importSettings.defaultVisitTypeId);

    const filteredCases = cases.filter(
      (x) =>
        !x.isClosed &&
        isWithinInterval(x.startDate, {
          start: addDays(
            patientInfo!.startDate,
            -importSettings!.caseSearchBackwardDaysCount,
          ),
          end: addDays(patientInfo!.startDate, 1),
        }),
    );
    let visitCase =
      filteredCases.length > 0
        ? filteredCases.reduce((a, b) => {
            return a.startDate > b.startDate ? a : b;
          })
        : null;

    try {
      isVisitCreating.current = true;
      let isNewCase = false;
      if (!visitCase) {
        const newCase = await QueryFactory.CaseClient.createCase({
          patientId: selectedPatient!.id,
          startDate: patientInfo!.startDate,
          endDate: null,
        });
        visitCase = {
          ...newCase,
          title: localizeCaseTitle(newCase),
        };
        isNewCase = true;
      }
      const visit = await QueryFactory.AppointmentEventsClient.createVisit({
        start: visitStart,
        end: visitEnd,
        caseId: visitCase.id,
        typeId: importSettings.defaultVisitTypeId,
        title: visitType ? i18next.t(visitType.value as any) : '',
        note: '',
        acceptConflict: true,
      });
      await visitsQuery.refetch();
      if (isNewCase) {
        await casesQuery.refetch();
      }
      setCurrentVisit(
        getVisitOption(visit, localizeVisitType(visitTypes, visit.typeId)),
      );
    } catch (e) {
      isVisitCreating.current = false;
      console.error('createNewVisit', e);
      await modals.showError({
        text: i18next.t('miniscreen.errors.failed_to_create_visit'),
      });
    }
  });

  useEffect(
    function clearPatientNotFoundError() {
      if (
        selectedPatient &&
        error?.message === i18next.t('miniscreen.errors.patient_is_not_found')
      ) {
        setError(undefined);
      }
    },
    [selectedPatient],
  );

  const addNewPatient = useEventCallback(
    async (patientInfo?: PatientInfo | null) => {
      const createdPatient = await globalForms.openPatientCreating(
        selectedPatient?.subtenantId,
        undefined,
        true,
        patientInfo,
      );
      if (createdPatient) {
        setSelectedPatient(createdPatient);
        isCaseCreating.current = false;
      }
    },
  );
  const modals = useModal();
  const addNewCase = useEventCallback(
    async (patientId?: number, startDate?: Date) => {
      if (!patientId) {
        return;
      }
      if (isCaseCreating.current) {
        return;
      }
      isCaseCreating.current = true;

      try {
        const newCase: CreateCaseDto = {
          patientId: patientId,
          startDate: startDate ?? startOfDay(new Date()),
          endDate: null,
        };
        const createdCase = await QueryFactory.CaseClient.createCase(newCase);
        const createdCaseData = {
          ...createdCase,
          title: localizeCaseTitle(createdCase),
          isCompleted: false,
        };

        await casesQuery.refetch();

        setTimeout(() => setCurrentCase(createdCaseData), 100);
      } catch (e) {
        console.error('addNewCase', e);
        isCaseCreating.current = false;
        await modals.showError({
          text: i18next.t('miniscreen.errors.failed_to_create_case'),
        });
      }
    },
  );

  const addNewVisit = useEventCallback(
    async (patientId?: number, caseId?: number) => {
      const newVisit = await globalForms.openVisitCreating(
        undefined,
        undefined,
        patientId,
        caseId,
      );
      if (newVisit) {
        setCurrentVisit(
          getVisitOption(
            newVisit,
            localizeVisitType(visitTypes, newVisit.typeId),
          ),
        );
      }
    },
  );

  async function setTemplateMeasurementType(measurement: MeasuredValue[]) {
    if (measurementTypes.length) {
      const templateType = getTemplateMeasurementType(
        measurement,
        measurementTypes,
      );
      if (templateType) {
        setMeasurementType(templateType);
      } else {
        setMeasurementType(
          // according to comment in #49502, we need to select PSG if we were unable to determine the measurement type
          // https://dev.azure.com/prismacode/Unicorn/_sprints/taskboard/Unicorn%20Team/Unicorn/Sprint%20112?workitem=49502#7363586
          measurementTypes.find((x) => x.value === 'PSG') ??
            measurementTypes[0],
        );
      }
    } else {
      setError(new Error(i18next.t('miniscreen.errors.no_measurement_types')));
    }
  }

  const setDefaultValues = () => {
    setError(undefined);
    setSelectedPatient(null);
    setPatientInfo(null);
  };

  const getPatients = useCallback(
    async (patientNumber?: string, searchString?: string) => {
      const patients = await QueryFactory.PatientClient.getPatients(
        undefined,
        searchString,
        undefined,
        undefined,
        undefined,
        patientNumber,
        undefined,
        [PatientState.Active],
        [PatientStatus.Active],
      );
      return patients.data ?? [];
    },
    [],
  );

  /**
   * Try to find existing patients according to patient data from measurement file
   */
  const findPatients = useCallback(
    async (patientInfo: PatientInfo): Promise<PatientListDto[]> => {
      // we try to find patients by patient number and full search string first (name + date of birth)
      let patients = await getPatients(
        patientInfo.number,
        `${patientInfo.firstName} ${patientInfo.lastName} ${
          patientInfo.dateOfBirth ? format(patientInfo.dateOfBirth, 'P') : ''
        }`,
      );
      if (patients.length) {
        return patients;
      }
      // if no result we try to search by patient number + name
      // if date of birth is not set then search string is same, so we can skip this step
      if (patientInfo.dateOfBirth) {
        patients = await getPatients(
          patientInfo.number,
          `${patientInfo.firstName} ${patientInfo.lastName}`,
        );
      }
      if (patients.length) {
        return patients;
      }
      // if no result try to search by name + date of birth (if set)
      patients = await getPatients(
        undefined,
        `${patientInfo.firstName} ${patientInfo.lastName} ${
          patientInfo.dateOfBirth ? format(patientInfo.dateOfBirth, 'P') : ''
        } `,
      );
      if (patients.length) {
        return patients;
      }
      // if no result we try to search by patient number only
      if (patientInfo.number) {
        patients = await getPatients(patientInfo.number);
      }
      return patients;
    },
    [],
  );

  /**
   * Set the last case to every patient in collection and return them
   */
  const getPatientsWithLastCases = useCallback(
    async (patients: PatientListDto[]): Promise<IPatientWithCase[]> => {
      const patientWithCases: IPatientWithCase[] = [];
      for (const patient of patients) {
        const patientCases = await QueryFactory.CaseClient.getCases(patient.id);
        const lastCase = patientCases.data?.length
          ? patientCases.data.reduce((a, b) => {
              return a.startDate > b.startDate ? a : b;
            })
          : undefined;
        const patientWithCase = { ...patient, lastCase };
        patientWithCases.push(patientWithCase);
      }
      return patientWithCases;
    },
    [],
  );

  async function parseFile(readFile: ReadFileData) {
    dispatch(MeasurementActions.importRestarted());

    setDefaultValues();
    if (!readFile) {
      return;
    }
    if (readFile.status === 'error') {
      console.log(readFile.error);
      setError(new Error("Can't read the file."));
    } else {
      try {
        console.log(`parseFile '${readFile.path}'`);
        const measurement = parseData(readFile.data);
        dispatch(MeasurementActions.measuredValuesLoaded(measurement));
        await setTemplateMeasurementType(measurement);

        console.log(`parseFile finished successfully`);
        return measurement;
      } catch (e: any) {
        setError(e);
        console.log(`parseFile finished with error ${e}`);
      }
    }
  }

  /**
   * Process parsed measurement data
   * @param data Values from measurement file
   */
  async function processData(data: MeasuredValue[]) {
    try {
      const selectedPatientInfo = await getPatientInfoFromMeasuredValues(data);
      setPatientInfo(selectedPatientInfo);

      const patients = await findPatients(selectedPatientInfo);
      if (patients.length) {
        if (patients.length === 1) {
          setSelectedPatient(patients[0]);
          return;
        }
        // if we found more than one patient we need to show them all in option list (including information about the last case)
        const patientWithCases: IPatientWithCase[] =
          await getPatientsWithLastCases(patients);
        setSuggestedPatients(patientWithCases);
      } else {
        setError(
          new Error(i18next.t('miniscreen.errors.patient_is_not_found')),
        );
        void addNewPatient(selectedPatientInfo);
      }
      console.log(`processData finished successfully`);
    } catch (e: any) {
      setIsInvalidPatientInfo(true);
      setError(new Error(e));
      console.log(`processData finished with error ${e}`);
    }
  }

  useEffect(
    function parseAndProcessData() {
      setError(undefined);
      if (!claims.permissions.importMeasurements) {
        setError(new Error(i18next.t('miniscreen.errors.no_permission')));
        return;
      }
      if (!readFile) {
        return;
      }
      loadBlocking(formId, i18next.t('miniscreen.loading'), async () => {
        if (measurementTypes.length > 0) {
          const data = await parseFile(readFile);
          if (data) {
            await processData(data);
          }
        }
      }).catch((e) =>
        console.error('MiniScreenImporter.parseAndProcessData', e),
      );
    },
    [loadBlocking, readFile, claims.permissions.importMeasurements, formId],
  );

  useEffect(() => {
    async function setExistingMeasurementIfFound() {
      if (!patientInfo || !selectedPatient || !currentVisit) {
        setExistingMeasurement(null);
        return;
      }
      //search the same measurements to replace
      const measurements = await QueryFactory.DataBlockClient.getWithValues(
        selectedPatient.id,
        null,
        currentVisit.id,
        null,
        null,
        DataBlockVariant.DeviceMeasurement,
        null,
        null,
        null,
        patientInfo!.startDate,
      );
      if (measurements.data.length) {
        const existingMeasurement = measurements.data[0];

        setCurrentCase(
          cases.find((x) => x.id === existingMeasurement.caseId) ?? null,
        );
        setCurrentVisit(
          visits.find((x) => x.id === existingMeasurement.visitId) ?? null,
        );
        setExistingMeasurement(measurements.data[0]);
        setMeasurementType(
          measurementTypes.find((x) => x.id === measurements.data[0].typeId),
        );
        openVisitOrMeasurement(
          selectedPatient.id,
          existingMeasurement.caseId!,
          existingMeasurement.visitId!,
          existingMeasurement.id,
        );
      } else {
        setExistingMeasurement(null);
      }
    }
    setExistingMeasurementIfFound();
  }, [patientInfo, selectedPatient, currentVisit]);

  useEffect(() => {
    if (selectedPatient && currentCase) {
      tabs.openTab(
        pages.patientCases.link({
          patientId: selectedPatient.id,
          caseId: currentCase.id,
        }),
      );
    }
    if (currentCase && currentVisit && currentCase.id !== currentVisit.caseId) {
      setCurrentVisit(null);
    }
  }, [currentCase]);

  useEffect(
    function openTabForCurrentVisit() {
      if (selectedPatient && currentVisit) {
        openVisitOrMeasurement(
          selectedPatient.id,
          currentVisit.caseId,
          currentVisit.id,
        );
      }
    },
    [currentVisit],
  );

  const openVisitOrMeasurement = useEventCallback(
    (
      patientId: number,
      caseId: number,
      visitId: number,
      measurementId?: number,
    ) => {
      const visitPrefix = `visit_${visitId}`;
      const measurementBlock = measurementId
        ? `_measurement_${measurementId}`
        : '';
      const item = `${visitPrefix}${measurementBlock}`;
      const search = `item=${item}`;
      const link = pages.patientCases.link({ patientId, caseId });
      tabs.openTab(link, search);
    },
  );

  const openMappings = useEventCallback(() => {
    tabs.openTab(
      pages.mappingsWithDeviceAndMeasurement.link({
        deviceId: DeviceModel.MiniScreen,
        measurementTypeId: selectedMeasurementType?.id,
      }),
    );
  });

  const isMeasurementValuesTheSame: boolean = useMemo(() => {
    if (
      !existingMeasurement ||
      !mappingsQuery.data ||
      !measurementFieldsQuery.data
    ) {
      return false;
    }
    try {
      const values: Record<string, string | number> = mapMeasuredValues(
        measuredValues,
        mappingsQuery.data ?? [],
        measurementFieldsQuery.data ?? [],
      );
      return getObjectHash(values) === existingMeasurement.valuesHash;
    } catch (e) {
      console.log(e);
      return false;
    }
  }, [existingMeasurement, mappingsQuery.data, measurementFieldsQuery.data]);

  const isReportFileTheSame = useMemo(() => {
    if (importSettings?.saveOldFilesWhenMeasurementReplaced) {
      // if turned on, we always save file as new version (and do not compare report files)
      return false;
    }
    if (
      !measuredValues ||
      !existingMeasurement ||
      !existingMeasurementFilesQuery.data
    ) {
      return false;
    }
    try {
      const templateName = getTemplateName(measuredValues);
      const fileName = `${templateName ?? selectedMeasurementType!.value}.pdf`;

      return existingMeasurementFilesQuery.data.some(
        (x) => x.name === fileName && !x.values?.['IsOutdated'],
      );
    } catch (e) {
      console.log(e);
      return false;
    }
  }, [existingMeasurement, existingMeasurementFilesQuery, measuredValues]);

  const isReImport = useMemo(() => {
    if (!existingMeasurement) {
      return false;
    }
    if (!isMeasurementValuesTheSame || isReportFileTheSame) {
      return true;
    }
    return false;
  }, [existingMeasurement, isMeasurementValuesTheSame, isReportFileTheSame]);

  const onCancel = useCallback(async () => {
    await tryCompleteForm(formType, formId, async () => undefined);
  }, [formType, formId, tryCompleteForm]);

  const importMiniScreenMeasurement = async () => {
    setDirty(true);
    if (!readFile) {
      console.log('attempt to start import, but no file is selected');
      return;
    }
    if (!patientInfo || !selectedPatient || !currentCase || !currentVisit) {
      return;
    }

    console.log('starting importing measurement');
    try {
      if (isReImport) {
        const isConfirmed = await modals.showConfirm({
          title: i18next.t('miniscreen.confirmations.re_import.title'),
          text: i18next.t('miniscreen.confirmations.re_import.text'),
          okButtonText: i18next.t(
            'miniscreen.confirmations.re_import.ok_button',
          ),
        });
        if (!isConfirmed) {
          return;
        }
      }
      await tryCompleteForm(formType, formId, async () => {
        const values: Record<string, string | number> = mapMeasuredValues(
          measuredValues,
          mappingsQuery.data ?? [],
          measurementFieldsQuery.data ?? [],
        );
        const valuesHash = getObjectHash(values);
        // measurement statistic

        if (selectedMeasurementType?.showStatistic) {
          const isStatisticCompleted =
            !!existingMeasurement &&
            existingMeasurement.isStatisticCompleted &&
            !isReImport;
          if (isStatisticCompleted) {
            const fields =
              await QueryFactory.DataBlockTypeClient.getUIConfigFields(
                existingMeasurement.typeId,
                UIConfigType.Statistic,
              );
            fields.forEach((field) => {
              const value = existingMeasurement.values[field.name];
              if (value !== undefined) {
                values[field.name] = value as string | number;
              }
            });
          } else {
            const statisticValues =
              await globalForms.openDataBlockStatisticCreating(
                selectedMeasurementType!.id,
                selectedPatient.id,
                currentCase.id,
                currentVisit?.id,
              );
            if (statisticValues) {
              Object.keys(statisticValues.fieldValues).forEach(
                (key: string) => {
                  const value = statisticValues.fieldValues[key];
                  if (value !== undefined) {
                    values[key] = value as string | number;
                  }
                },
              );
            } else {
              throw new Error(i18next.t('miniscreen.errors.statistic'));
            }
          }
        }

        const pdfFilePath = await getPdfFilePath(measuredValues);
        const templateName = getTemplateName(measuredValues);
        if (!pdfFilePath && !pdfFile) {
          toast(i18next.t('miniscreen.toasts.not_found_file'), {
            type: 'warning',
          });
        }

        const data: ImportData = {
          isReImport: isReImport,
          replacedMeasurement: existingMeasurement,
          patientId: selectedPatient.id,
          caseId: currentCase.id,
          visitId: currentVisit?.id,
          deviceModel: defaultDeviceModel,
          typeId: selectedMeasurementType!.id,
          values: values,
          sdbdataFilePath: readFile.path,
          pdfFilePath: pdfFilePath,
          pdfFile: pdfFile ?? undefined,
          pdfFileName: `${templateName ?? selectedMeasurementType!.value}.pdf`,
          startDate: patientInfo.startDate,
          endDate: patientInfo.endDate,
          file: importFile,
          isStatisticCompleted: selectedMeasurementType?.showStatistic ?? false,
          valuesHash: valuesHash,
          settings: importSettings!,
        };

        const measurementId = await importMeasurement(data);

        if (pdfFilePath) {
          deleteMiniScreenPdfFile(pdfFilePath).catch((e) => Logger.error(e));
        }
        dispatch(MeasurementActions.importFinished());
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey(
            null,
            currentCase.id,
            null,
            null,
          ),
        });
        await queryClient.invalidateQueries({
          queryKey:
            QueryFactory.DataBlockQuery.getDataBlockQueryKey(measurementId),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.FileQuery.searchFilesQueryKey({
            dataBlockId: measurementId,
          }),
        });
        toast(i18next.t('miniscreen.toasts.import_successful'), {
          type: 'success',
        });
        openVisitOrMeasurement(
          selectedPatient.id,
          currentCase.id,
          currentVisit?.id,
          measurementId,
        );
      });
    } catch (error) {
      console.error('importMiniScreenMeasurement', error);
      setError(new Error(parseError(error)));
    }
  };

  if (measurementTypes.length === 0) {
    globalForms.closeForm(props.formId);
    void modals.showError({
      text: i18next.t('miniscreen.errors.all_device_measurements_disabled'),
    });
    return null;
  }

  return (
    <Loading loading={isSettingsQueryLoading}>
      <div className={styles.container} data-test-id="miniscreen-import-popup">
        <div className={styles.content} aria-disabled={isInvalidPatientInfo}>
          <div className={styles.patientData}>
            <Field
              title={i18next.t('miniscreen.patient')}
              linkProps={{
                title: i18next.t('miniscreen.controls.add_patient'),
                onClick: () => addNewPatient(),
                disabled: isInvalidPatientInfo || !!existingMeasurement,
              }}
            >
              {!existingMeasurement ? (
                <PatientSearch
                  isOnlyActive={true}
                  onSelectedPatientChanged={(selectedPatient) => {
                    if (isInvalidPatientInfo) {
                      return;
                    }
                    isCaseCreating.current = false;
                    setSelectedPatient(selectedPatient as any);
                    if (!selectedPatient) {
                      setCurrentCase(null);
                      setCurrentVisit(null);
                    }
                  }}
                  selectedPatient={selectedPatient}
                  renderInput={InputComponentForPatientSearch}
                  patientsLimit={9}
                  errorText={
                    isDirty && !selectedPatient
                      ? i18next.t('errors.required')
                      : undefined
                  }
                  autoFocus={true}
                  predefinedPatients={suggestedPatients}
                  variant={'formInput'}
                />
              ) : (
                <Input disabled={true} value={getFullName(selectedPatient)} />
              )}
            </Field>
            <Field
              className={styles.field}
              title={i18next.t('miniscreen.case')}
              linkProps={{
                disabled: !selectedPatient || !!existingMeasurement,
                title: i18next.t('miniscreen.controls.add_case'),
                onClick: () => {
                  isCaseCreating.current = false;
                  isVisitCreating.current = true;
                  void addNewCase(selectedPatient?.id);
                },
              }}
            >
              <DropDownInput
                useIdFunctionAsValue={true}
                required
                variant="formInput"
                getOptionLabel={'title'}
                idFunction={'id'}
                options={cases}
                getOptionDisabled={(item) => item.isClosed}
                value={currentCase?.id || null}
                onValueChanged={(selectedCase) => {
                  isVisitCreating.current = true;
                  setCurrentCase(
                    cases.find(
                      (patientCase) => patientCase.id === selectedCase.id,
                    ) ?? null,
                  );
                }}
                errorText={
                  isDirty && !currentCase
                    ? i18next.t('errors.required')
                    : undefined
                }
                disabled={!!existingMeasurement} // we update existing measurement, changing of case and visit is not allowed
              />
            </Field>
            <Field
              className={styles.field}
              title={i18next.t('miniscreen.visit')}
              linkProps={{
                disabled:
                  !selectedPatient || !currentCase || !!existingMeasurement,
                title: i18next.t('miniscreen.controls.add_visit'),
                onClick: () =>
                  addNewVisit(selectedPatient?.id, currentCase?.id),
              }}
            >
              <DropDownInput
                useIdFunctionAsValue={true}
                required={false}
                variant="formInput"
                getOptionLabel="title"
                idFunction="id"
                options={visits}
                value={currentVisit?.id || null}
                onValueChanged={(selectedVisit) =>
                  setCurrentVisit(selectedVisit)
                }
                errorText={
                  isDirty && !currentVisit
                    ? i18next.t('errors.required')
                    : undefined
                }
                disabled={!!existingMeasurement}
              />
            </Field>
            {(error || !!existingMeasurement) && (
              <div>
                <div
                  className={styles.patientErrorTitleContainer}
                  data-test-id="miniscreen-popup-error-title"
                >
                  <ErrorIcon className={styles.patientErrorIcon} />
                  <div className={styles.patientErrorTitle}>
                    {!!existingMeasurement
                      ? i18next.t(
                          'miniscreen.warnings.measurement_already_exists',
                        )
                      : i18next.t('miniscreen.errors.import_is_not_possible')}
                  </div>
                </div>
                <div
                  className={styles.patientErrorMessage}
                  data-test-id="miniscreen-popup-error-message"
                >
                  {!!error && <span>{error.message}</span>}
                  {!!existingMeasurement && (
                    <span>
                      {isMeasurementValuesTheSame
                        ? isReportFileTheSame
                          ? i18next.t(
                              'miniscreen.warnings.measurement_values_and_report_the_same',
                            )
                          : i18next.t(
                              'miniscreen.warnings.measurement_values_the_same_only_add_report',
                            )
                        : i18next.t(
                            'miniscreen.warnings.measurement_values_not_the_same_only_re_import',
                          )}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className={styles.settings}>
            <div className={styles.settingsTitleContainer}>
              <div className={styles.settingsTitle}>
                {i18next.t('miniscreen.settings_title')}
              </div>
              {!!claims.permissions.editMeasurementMappings &&
                !!selectedMeasurementType && (
                  <IconButton
                    color="grey"
                    iconComponent={<SettingsIcon />}
                    className={styles.settingsButton}
                    onClick={openMappings}
                  />
                )}
            </div>
            <div className={styles.radioButtonGroup}>
              {measurementTypes.map((measurementType, index) => (
                <SelectTypeRadioButton
                  position={
                    index === 0
                      ? 'left'
                      : index === measurementTypes.length - 1
                        ? 'right'
                        : 'center'
                  }
                  title={measurementType.value}
                  key={measurementType.id}
                  selected={selectedMeasurementType?.id === measurementType.id}
                  onChange={() => setMeasurementType(measurementType)}
                  disabled={isInvalidPatientInfo}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={styles.import} data-test-id="button-container">
          <div className={styles.buttonContainer}>
            <Button
              title={i18next.t('miniscreen.controls.cancel_button')}
              color={ButtonColor.Primary}
              onClick={onCancel}
            />
            <Button
              title={
                !!existingMeasurement
                  ? isReImport
                    ? i18next.t('miniscreen.controls.re_import_button')
                    : i18next.t('miniscreen.controls.add_report_button')
                  : i18next.t('miniscreen.controls.import_button')
              }
              color={ButtonColor.Default}
              className={styles.importButton}
              onClick={importMiniScreenMeasurement}
              disabled={
                isInvalidPatientInfo ||
                (isMeasurementValuesTheSame && isReportFileTheSame)
              }
            />
          </div>
        </div>
      </div>
    </Loading>
  );
};

export default MiniScreenImporter;
